import { ArenaWaveEntry, EnemyType } from './types';
import { SpawnGroup } from './waves';

/**
 * Test Arena wave mixing.
 *
 * A wave is built from entries — "3 of these, 5 of those" — and the enemies are
 * interleaved rather than marched out one full group at a time: 3×A + 3×B walks
 * out as ABABAB, not AAABBB.
 */

/**
 * Merge entries into a single spawn order, spreading each type evenly across the
 * whole wave in proportion to its count.
 *
 * Each of an entry's N enemies claims the fractional position (i + 0.5) / N —
 * the midpoint of its share of the wave — and every enemy is then sorted onto
 * that shared 0..1 timeline. Equal counts land on identical positions and are
 * broken by entry order, so 3×A + 3×B gives ABABAB; unequal counts spread the
 * rarer type through the commoner one (2×A + 3×B gives BABAB).
 *
 * Pure and deterministic: same entries in, same order out.
 */
export function interleaveArenaWave(entries: ArenaWaveEntry[]): EnemyType[] {
  const slots: { pos: number; order: number; type: EnemyType }[] = [];
  entries.forEach((entry, order) => {
    for (let i = 0; i < entry.count; i++) {
      slots.push({ pos: (i + 0.5) / entry.count, order, type: entry.enemyType });
    }
  });
  // The `order` tiebreak makes the equal-count case (exact positional ties)
  // resolve to entry order rather than relying on sort stability.
  slots.sort((a, b) => a.pos - b.pos || a.order - b.order);
  return slots.map((s) => s.type);
}

/**
 * The interleaved wave as spawn groups: one single-enemy group per spawn, laid
 * out `interval` seconds apart. One group per enemy — rather than one per type —
 * because a mixed order is not a set of evenly-spaced-per-type streams, so each
 * spawn needs its own instant.
 */
export function arenaWaveGroups(entries: ArenaWaveEntry[], interval: number): SpawnGroup[] {
  return interleaveArenaWave(entries).map((enemyType, i) => ({
    enemyType,
    count: 1,
    interval,
    startDelay: i * interval,
  }));
}

/** Total enemies a wave will spawn. */
export function arenaWaveSize(entries: ArenaWaveEntry[]): number {
  return entries.reduce((n, e) => n + e.count, 0);
}

/**
 * The first `limit` enemies of the interleaved order, WITHOUT materialising the
 * whole wave. Each entry's slot positions (i + 0.5)/count are increasing, so
 * only its first `limit` slots can appear among the wave's first `limit` —
 * sorting `limit × entries` slots instead of `arenaWaveSize()` keeps the
 * dialog's SPAWN ORDER preview cheap now that counts are uncapped.
 */
export function arenaWavePreview(entries: ArenaWaveEntry[], limit: number): EnemyType[] {
  const slots: { pos: number; order: number; type: EnemyType }[] = [];
  entries.forEach((entry, order) => {
    for (let i = 0; i < Math.min(entry.count, limit); i++) {
      slots.push({ pos: (i + 0.5) / entry.count, order, type: entry.enemyType });
    }
  });
  slots.sort((a, b) => a.pos - b.pos || a.order - b.order);
  return slots.slice(0, limit).map((s) => s.type);
}
